import classNames from 'classnames';
import { bool, func, node, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { Field } from 'react-final-form';
import { ExpandingTextarea, ValidationError } from '../../components';

import { FieldLabel } from './FieldLabel/FieldLabel';
import css from './FieldTextInput.module.css';

const CONTENT_MAX_LENGTH = 5000;

const FieldTextInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    customErrorText,
    id,
    label,
    supportingText,
    helpText,
    input,
    meta,
    onUnmount,
    isUncontrolled,
    inputRef,
    hideErrorMessage,
    inputPrefix,
    inputPrefixTestId,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const isTextarea = input.type === 'textarea';
  const { onBlur: extraOnBlur, ...restWithoutOnBlur } = rest;

  const errorText = customErrorText || error;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = !!customErrorText || !!(touched && invalid && error);

  const fieldMeta = { touched: hasError, error: errorText };

  // Textarea doesn't need type.
  const { type, ...inputWithoutType } = input;
  // Uncontrolled input uses defaultValue instead of value.
  const { value: defaultValue, ...inputWithoutValue } = input;
  // Use inputRef if it is passed as prop.
  const refMaybe = inputRef ? { ref: inputRef } : {};
  const ariaDescribedbyIds = [];
  if (supportingText) ariaDescribedbyIds.push(`SupportingText_${id}`);
  if (helpText) ariaDescribedbyIds.push(`HelpText_${id}`);
  const ariaDescribedbyMaybe = ariaDescribedbyIds.length
    ? { ['aria-describedby']: ariaDescribedbyIds.join(' ') }
    : null;

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
      [css.textarea]: isTextarea,
    });
  const maxLength = CONTENT_MAX_LENGTH;

  const composedOnBlur = event => {
    if (typeof input.onBlur === 'function') {
      input.onBlur(event);
    }
    if (typeof extraOnBlur === 'function') {
      extraOnBlur(event);
    }
  };

  const defaultInputProps = {
    className: inputClasses,
    id,
    ...ariaDescribedbyMaybe,
  };

  const inputProps = isTextarea
    ? {
        ...defaultInputProps,
        rows: 1,
        maxLength,
        ...refMaybe,
        ...inputWithoutType,
        ...restWithoutOnBlur,
        onBlur: composedOnBlur,
      }
    : isUncontrolled
    ? {
        ...defaultInputProps,
        type,
        defaultValue,
        ...refMaybe,
        ...inputWithoutValue,
        ...restWithoutOnBlur,
        onBlur: composedOnBlur,
      }
    : {
        className: inputClasses,
        id,
        type,
        ...refMaybe,
        ...input,
        ...restWithoutOnBlur,
        onBlur: composedOnBlur,
      };

  const classes = classNames(rootClassName || css.root, className, {
    [css.hasHelpText]: !!helpText,
  });
  const control = isTextarea ? <ExpandingTextarea {...inputProps} /> : <input {...inputProps} />;
  const controlWithPrefix =
    inputPrefix && !isTextarea ? (
      <div className={css.inputWithPrefix} dir="ltr">
        <span className={css.inputPrefix} data-testid={inputPrefixTestId} lang="en">
          {inputPrefix}
        </span>
        {control}
      </div>
    ) : (
      control
    );
  return (
    <div className={classes}>
      <FieldLabel label={label} id={id} supportingText={supportingText} />
      {controlWithPrefix}
      {helpText ? (
        <div className={css.helpText} id={`HelpText_${id}`}>
          {helpText}
        </div>
      ) : null}
      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

FieldTextInputComponent.defaultProps = {
  rootClassName: null,
  className: null,
  inputRootClass: null,
  onUnmount: null,
  customErrorText: null,
  id: null,
  label: null,
  supportingText: null,
  helpText: null,
  isUncontrolled: false,
  inputRef: null,
  inputPrefix: null,
  inputPrefixTestId: null,
};

FieldTextInputComponent.propTypes = {
  rootClassName: string,
  className: string,
  inputRootClass: string,

  onUnmount: func,

  // Error message that can be manually passed to input field,
  // overrides default validation message
  customErrorText: string,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,
  supportingText: string,
  // Additional helper text shown below the input element
  helpText: string,

  // Uncontrolled input uses defaultValue prop, but doesn't pass value from form to the field.
  // https://reactjs.org/docs/uncontrolled-components.html#default-values
  isUncontrolled: bool,
  // a ref object passed for input element.
  inputRef: object,
  // Fixed text rendered immediately before the input (e.g. soulwise.io/u/).
  inputPrefix: node,
  inputPrefixTestId: string,

  // Generated by final-form's Field component
  input: shape({
    onChange: func.isRequired,
    // Either 'textarea' or something that is passed to the input element
    type: string.isRequired,
  }).isRequired,
  meta: object.isRequired,
};

class FieldTextInput extends Component {
  componentWillUnmount() {
    // Unmounting happens too late if it is done inside Field component
    // (Then Form has already registered its (new) fields and
    // changing the value without corresponding field is prohibited in Final Form
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={FieldTextInputComponent} {...this.props} />;
  }
}

export default FieldTextInput;
